import React, { useState, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import {Search, SlidersHorizontal, X} from 'lucide-react';
import DestinationCard from '../components/DestinationCard';
import Japan from './destinations/Japan';
import Tokyo from './destinations/Tokyo';
import Osaka from './destinations/Osaka';
import Kyoto from './destinations/Kyoto';
import JapanThemeParks from './destinations/JapanThemeParks';
import Singapore from './destinations/Singapore';
import HongKong from './destinations/HongKong';
import Orlando from './destinations/Orlando';
import Anaheim from './destinations/Anaheim';

function DestinationsOverview() {
  const [searchQuery, setSearchQuery] = useState('');
  const [budgetFilter, setBudgetFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const destinations = [
    {
      name: 'Japan',
      slug: '/destinations/japan',
      image: 'https://images.pexels.com/photos/161401/fushimi-inari-taisha-shrine-kyoto-japan-temple-161401.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: 'Ancient temples, cutting-edge technology, world-class cuisine, and unique cultural experiences blend seamlessly.',
      bestTime: 'Mar-May, Sep-Nov',
      budget: '$80-150/day',
      budgetRange: 'medium',
      months: ['mar', 'apr', 'may', 'sep', 'oct', 'nov'],
      highlights: ['Tokyo Skytree', 'Kyoto Temples', 'Mt. Fuji', 'Universal Studios Japan', 'Cherry Blossoms']
    },
    {
      name: 'Singapore',
      slug: '/destinations/singapore',
      image: 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: 'A modern marvel where cultures collide, offering futuristic attractions, world-class food, and tropical gardens.',
      bestTime: 'Feb-Apr',
      budget: '$80-150/day',
      budgetRange: 'medium',
      months: ['feb', 'mar', 'apr'],
      highlights: ['Gardens by the Bay', 'Marina Bay Sands', 'Universal Studios', 'Hawker Centers', 'Singapore Zoo']
    },
    {
      name: 'Hong Kong',
      slug: '/destinations/hong-kong',
      image: 'https://images.pexels.com/photos/1525041/pexels-photo-1525041.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: 'Skyscrapers meet mountains in this vibrant city blending British colonial history with Chinese culture.',
      bestTime: 'Oct-Dec',
      budget: '$70-130/day',
      budgetRange: 'medium',
      months: ['oct', 'nov', 'dec'],
      highlights: ['Victoria Peak', 'Star Ferry', 'Big Buddha', 'Temple Street Market', 'Disneyland']
    },
    {
      name: 'Orlando, USA',
      slug: '/destinations/usa/orlando',
      image: 'https://images.pexels.com/photos/358312/pexels-photo-358312.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: 'Theme park capital of the world with Disney World, Universal, and endless entertainment for all ages.',
      bestTime: 'Jan-Feb, Sep-Nov',
      budget: '$150-300/day',
      budgetRange: 'high',
      months: ['jan', 'feb', 'sep', 'oct', 'nov'],
      highlights: ['Walt Disney World', 'Universal Orlando', 'Harry Potter World', 'SeaWorld', 'Kennedy Space Center']
    }
  ];

  const filteredDestinations = useMemo(() => {
    return destinations.filter(dest => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        dest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dest.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dest.highlights.some(h => h.toLowerCase().includes(searchQuery.toLowerCase()));

      // Budget filter
      const matchesBudget = budgetFilter === 'all' || dest.budgetRange === budgetFilter;

      // Season filter
      const matchesSeason = seasonFilter === 'all' || dest.months.includes(seasonFilter);

      return matchesSearch && matchesBudget && matchesSeason;
    });
  }, [searchQuery, budgetFilter, seasonFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setBudgetFilter('all');
    setSeasonFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || budgetFilter !== 'all' || seasonFilter !== 'all';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <img
          src="https://images.pexels.com/photos/346885/pexels-photo-346885.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Destinations"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Explore Destinations</h1>
            <p className="text-xl text-white/90 max-w-2xl">
              Discover curated guides to the world's most exciting destinations. Get just enough information to plan your perfect trip.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search destinations, attractions, or experiences..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-[#4A90E2] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              Filters
              {hasActiveFilters && !showFilters && (
                <span className="bg-white text-[#4A90E2] text-xs font-bold px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Budget Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Budget Range
                  </label>
                  <select
                    value={budgetFilter}
                    onChange={(e) => setBudgetFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2]"
                  >
                    <option value="all">All Budgets</option>
                    <option value="medium">$70-150/day</option>
                    <option value="high">$150+/day</option>
                  </select>
                </div>

                {/* Season Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Best Time to Visit
                  </label>
                  <select
                    value={seasonFilter}
                    onChange={(e) => setSeasonFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90E2]"
                  >
                    <option value="all">Any Time</option>
                    <option value="jan">January</option>
                    <option value="feb">February</option>
                    <option value="mar">March</option>
                    <option value="apr">April</option>
                    <option value="may">May</option>
                    <option value="sep">September</option>
                    <option value="oct">October</option>
                    <option value="nov">November</option>
                    <option value="dec">December</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-sm text-[#FF6B35] hover:text-[#0A2540] font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && !showFilters && (
            <div className="flex flex-wrap gap-2 mt-4">
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#4A90E2]/10 text-[#4A90E2] rounded-full text-sm">
                  Search: {searchQuery}
                  <button onClick={() => setSearchQuery('')} className="hover:text-[#0A2540]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {budgetFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#F7B32B]/10 text-[#F7B32B] rounded-full text-sm">
                  Budget: {budgetFilter === 'medium' ? '$70-150/day' : '$150+/day'}
                  <button onClick={() => setBudgetFilter('all')} className="hover:text-[#0A2540]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {seasonFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#FF6B35]/10 text-[#FF6B35] rounded-full text-sm">
                  Month: {seasonFilter.charAt(0).toUpperCase() + seasonFilter.slice(1)}
                  <button onClick={() => setSeasonFilter('all')} className="hover:text-[#0A2540]">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Destinations Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-[#0A2540] mb-2">
            {filteredDestinations.length === destinations.length
              ? 'Featured Destinations'
              : `${filteredDestinations.length} Destination${filteredDestinations.length !== 1 ? 's' : ''} Found`}
          </h2>
          <p className="text-gray-600">
            {filteredDestinations.length === 0
              ? 'No destinations match your current filters. Try adjusting your search criteria.'
              : 'From ancient temples to modern theme parks, these destinations offer unforgettable experiences for every type of traveler.'}
          </p>
        </div>

        {filteredDestinations.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-8">
            {filteredDestinations.map((dest) => (
              <DestinationCard key={dest.slug} {...dest} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No results found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters or search terms</p>
            <button
              onClick={clearFilters}
              className="px-6 py-3 bg-[#FF6B35] text-white rounded-lg hover:bg-[#0A2540] transition-colors font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Destinations() {
  return (
    <Routes>
      <Route index element={<DestinationsOverview />} />
      <Route path="japan" element={<Japan />} />
      <Route path="japan/tokyo" element={<Tokyo />} />
      <Route path="japan/osaka" element={<Osaka />} />
      <Route path="japan/kyoto" element={<Kyoto />} />
      <Route path="japan/theme-parks" element={<JapanThemeParks />} />
      <Route path="singapore" element={<Singapore />} />
      <Route path="hong-kong" element={<HongKong />} />
      <Route path="usa/orlando" element={<Orlando />} />
      <Route path="usa/anaheim" element={<Anaheim />} />
    </Routes>
  );
}
